<template>
  <div class="md:w-368 mx-auto">
    <JobPostScheduleCard
      id="schedule-type-nanny"
      title="Nanny"
      body="Ongoing, usually 2-5 full weekdays"
      :shadow="false"
      class="mb-16"
    >
      <NannyScheduleIcon />
    </JobPostScheduleCard>

    <Card class="w-full md:p-24">
      <form>
        <fieldset class="mb-16">
          <legend id="schedule-type" class="mb-8 text-strong-body">Schedule type</legend>
          <div class="flex">
            <RadioButton
              id="part-time"
              v-model="nannyScheduleType"
              data-test-id="part-time"
              radioName="schedule_type"
              labelName="Part-time"
              :radioValue="EmploymentTypes.PART_TIME"
              :checked="nannyScheduleType == EmploymentTypes.PART_TIME"
              class="mr-32 pb-24 md:pb-16"
            />
            <RadioButton
              id="full-time"
              v-model="nannyScheduleType"
              data-test-id="full-time"
              radioName="schedule_type"
              labelName="Full-time"
              :radioValue="EmploymentTypes.FULL_TIME"
              :checked="nannyScheduleType == EmploymentTypes.FULL_TIME"
              class="pb-24 md:pb-16"
            />
          </div>
          <div>
            <poppins-date-picker
              id="nanny-schedule-datepicker"
              v-model="startDate"
              data-test-id="nanny-schedule-datepicker"
              labelName="Start date"
              :minDate="minStartDate"
              :maxDate="maxStartDate"
              :status="datepickerStatus"
              :helperText="errors.startDate"
              @input="$delete(errors, 'startDate')"
            />
          </div>

          <Checkbox
            v-model="startDateFlexible"
            data-test-id="start-date-flexible"
            class="mt-16"
            labelName="Start date is flexible"
            checkboxId="start-date-flexible"
          />
        </fieldset>

        <fieldset>
          <legend id="day-times" class="mb-8 text-strong-body">Days and time</legend>

          <fieldset>
            <legend class="mb-8 text-subtitle font-medium">Weekdays (Monday–Friday):</legend>
            <Checkbox
              key="weekday-morning"
              v-model="nannyDayTimes"
              data-test-id="weekday-morning"
              labelName="Morning (before school)"
              checkboxId="weekday-morning"
              :value="NannyHours.WEEKDAY_MORNING"
            />
            <Checkbox
              key="weekday-daytime"
              v-model="nannyDayTimes"
              data-test-id="weekday-daytime"
              labelName="Daytime"
              checkboxId="weekday-daytime"
              :value="NannyHours.WEEKDAY_DAY"
            />
            <Checkbox
              key="weekday-afternoon"
              v-model="nannyDayTimes"
              data-test-id="weekday-afternoon"
              labelName="Afternoon (after school)"
              checkboxId="weekday-afternoon"
              :value="NannyHours.WEEKDAY_AFTERNOON"
            />
            <Checkbox
              key="weekday-evening"
              v-model="nannyDayTimes"
              data-test-id="weekday-evening"
              labelName="Evening"
              checkboxId="weekday-evening"
              :value="NannyHours.WEEKDAY_EVENING"
            />
          </fieldset>

          <fieldset>
            <legend class="mb-8 text-subtitle font-medium">Weekends (Saturday, Sunday):</legend>
            <Checkbox
              key="weekend-daytime"
              v-model="nannyDayTimes"
              data-test-id="weekend-daytime"
              labelName="Daytime"
              checkboxId="weekend-daytime"
              :value="NannyHours.WEEKENDS_DAY"
            />
            <Checkbox
              key="weekend-evening"
              v-model="nannyDayTimes"
              data-test-id="weekend-evening"
              labelName="Evening"
              checkboxId="weekend-evening"
              :value="NannyHours.WEEKENDS_EVENING"
            />
          </fieldset>
        </fieldset>

        <HelperText v-if="errors.dayTimes" :text="errors.dayTimes" :status="HelperStatus.Error" />

        <div class="flex flex-row-reverse mt-32">
          <RouteStepperNextButton
            :stepData="reviewSection"
            :draftData="draftData"
            :validate="validate"
            @errors="errors = $event"
          />
        </div>
      </form>
    </Card>
  </div>
</template>

<script lang="ts">
import RadioButton from '@/components/radio_button/RadioButton.vue';
import PoppinsDatePicker from '@/components/PoppinsDatePicker/PoppinsDatePicker.vue';
import Checkbox from '@/components/checkbox/Checkbox.vue';
import NannyScheduleIcon from '@/icons/NannyScheduleIcon.vue';
import JobPostScheduleCard from './JobPostScheduleCard.vue';
import { minStartDate, maxStartDate, status, helperText } from './schedule_helpers';
import Card from '@/components/card/Card.vue';
import JobPostScheduleNannyReview from './JobPostScheduleNannyReview.vue';
import { ReviewSection } from '@/components/route_stepper/types';
import { NannyJobSchedule, EmploymentTypes, NannyHours } from '@/store/modules/job/types';
import { JobPostDraft } from '../types';
import helperStatus, { HelperStatus } from '@/helpers/status';
import RouteStepperNextButton from '@/components/route_stepper/RouteStepperNextButton.vue';
import HelperText from '@/components/helper_text/HelperText.vue';
import { format, formatISO } from 'date-fns';

interface Errors {
  startDate?: string;
  scheduleType?: string;
  dayTimes?: string;
}

interface JobPostScheduleNannyData {
  nannyScheduleType: EmploymentTypes;
  startDate: Date | null;
  startDateFlexible: boolean;
  nannyDayTimes: NannyHours[];
  errors: Errors;
}

export default {
  name: 'JobPostScheduleNanny',
  components: {
    RadioButton,
    PoppinsDatePicker,
    NannyScheduleIcon,
    JobPostScheduleCard,
    Checkbox,
    Card,
    RouteStepperNextButton,
    HelperText,
  },
  data(): JobPostScheduleNannyData {
    return {
      nannyScheduleType: EmploymentTypes.PART_TIME,
      startDate: null,
      startDateFlexible: false,
      nannyDayTimes: [],
      errors: {},
    };
  },
  computed: {
    minStartDate,
    maxStartDate,
    HelperStatus(): typeof HelperStatus {
      // this is needed to relay the enum import to the template
      return HelperStatus;
    },
    EmploymentTypes(): typeof EmploymentTypes {
      // this is needed to relay the enum import to the template
      return EmploymentTypes;
    },
    NannyHours(): typeof NannyHours {
      // this is needed to relay the enum import to the template
      return NannyHours;
    },
    datepickerStatus(): helperStatus {
      if (this.errors.startDate) {
        return helperStatus.Error;
      }
      return helperStatus.Neutral;
    },
    reviewSection(): ReviewSection | undefined {
      if (!this.startDate) {
        return undefined;
      }

      return {
        title: 'Schedule',
        component: JobPostScheduleNannyReview,
        props: {
          formattedStartDate: format(this.startDate, 'eeee, MMMM d, yyyy'),
          startDateFlexible: this.startDateFlexible,
          nannyScheduleType: this.nannyScheduleType,
          nannyDayTimes: this.nannyDayTimes,
        },
        editPath: this.$route.path,
      };
    },
    nannyJobSchedule(): NannyJobSchedule | undefined {
      if (!this.startDate) {
        return undefined;
      }

      return {
        startDate: formatISO(this.startDate, { representation: 'date' }),
        startTime: formatISO(this.startDate), // there isn't currently a required start time field for nanny jobs
        endTime: formatISO(this.startDate), // there isn't currently a required end time field for nanny jobs
        isStartDateFlexible: this.startDateFlexible,
        employmentType: this.nannyScheduleType,
        hours: this.nannyDayTimes,
      };
    },
    draftData(): JobPostDraft {
      return {
        schedule: { nannyJobSchedule: this.nannyJobSchedule },
      };
    },
  },
  watch: {
    nannyDayTimes() {
      this.errors.dayTimes = undefined;
    },
  },
  methods: {
    status,
    helperText,
    validate(): Errors {
      const errors: Record<string, string> = {};

      if (!this.startDate) {
        errors.startDate = 'This field is required.';
      }

      if (!this.nannyDayTimes.length) {
        errors.dayTimes = 'Selecting at least one day and time is required.';
      }

      return errors;
    },
  },
};
</script>
